// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Target } from '../../client';
import { progressivelyOpenLineUpView } from '../../modules/line-up/actions/lineUp';
import LineUpOpenViewButton from '../../modules/line-up/components/LineUpOpenViewButton/LineUpOpenViewButton';
import { SportsWithLineUpNewTag } from '../../modules/line-up/constants/runtime';
import theme from '../../styled-components/dark';
import { type SportId } from '../../types/sports';
import BetGroupFactoryContainer from '../BetGroupFactory/BetGroupFactoryContainer';
import Link from '../Link';
import CardHeaderContainer from '../MatchCardHeader/MatchCardHeaderContainer';
import MatchCardMoreBetsButton from '../MatchCardMoreBetsButton';
import ScoreboardMultimatchContainer from '../ScoreboardMultimatch/ScoreboardMultimatchContainer';

const StyledLink = styled(Link)`
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  font-size: ${Target.__MOBILE__ ? 1 : 1.1}em;
`;

const Wrapper = styled.div`
  padding: ${Target.__MOBILE__ ? '0 5px 10px 5px' : '10px'};
  margin: ${Target.__MOBILE__ ? '0 5px' : '0'};
  transform: translate3d(0, 0, 0);
`;

const WrapTitle = styled.div`
  position: relative;
  margin: 0.4em 0 0.8em;
`;

const StyledCardHeader = styled(CardHeaderContainer)`
  font-size: 0.8em;
  height: 2em;
  z-index: 10;
`;

const InnerWrap = styled.div`
  padding: 0.4em;
  background: ${theme.MatchCard.backgroundColor};
  border-radius: 0 0 1em 1em;
`;

const LabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.2em 0;
  padding: 0 2.5em 0 2.2em;

  font-weight: bold;
  font-size: 1.05em;
  color: ${theme.MatchTitle.competitor.color};
`;

const AbsoluteRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.div`
  width: 100%;
  padding-top: 33.33%;
  position: relative;
  pointer-events: none;
  z-index: 9;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  pointer-events: none;
`;

const StyledScoreboardMultimatchContainer = styled(ScoreboardMultimatchContainer)`
  font-size: 1em;
`;

type Props = {
  className?: string;
  matchId: number;
  image: string | null;
  startDate: number;
  moreBets: number;
  tvChannel?: string;
  isFavorite: boolean;
  isLive?: boolean;
  showFavorite: boolean;
  betId: number;
  title: string;
  sportId: SportId;
  onFavoriteButtonClicked: () => void | null;
  to: string;
  categoryId: number;
  betCategoryId?: number;
  expandId: string;
  areMatchLineUpsAvailable?: boolean;
};

const MatchCardOutright = ({
  className,
  matchId,
  image,
  startDate,
  moreBets = 0,
  tvChannel,
  isFavorite,
  isLive,
  showFavorite,
  betId,
  title,
  sportId,
  onFavoriteButtonClicked,
  to,
  categoryId,
  betCategoryId,
  expandId,
  areMatchLineUpsAvailable = false,
}: Props) => {
  const betsData = useMemo(() => [{ betId }], [betId]);
  const dispatch = useDispatch();

  const onLineUpRedirection = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      dispatch(progressivelyOpenLineUpView(matchId));
    },
    [dispatch, matchId],
  );

  return (
    <Wrapper className={className}>
      <StyledLink to={to}>
        <StyledCardHeader
          onFavoriteButtonClicked={onFavoriteButtonClicked}
          showFavorite={showFavorite}
          sportId={sportId}
          startDate={startDate}
          tvChannel={tvChannel}
          isFavorite={isFavorite}
          isLive={isLive}
          matchId={matchId}
          cardBadgeHasAttach
          isOutright
          categoryId={categoryId}
        />
        {image ? (
          <ImageContainer>
            <Image src={image} />
          </ImageContainer>
        ) : null}
        <InnerWrap>
          <WrapTitle>
            <LabelWrapper>{title}</LabelWrapper>
            <StyledScoreboardMultimatchContainer matchId={matchId} />
            <AbsoluteRight>
              {areMatchLineUpsAvailable ? (
                <LineUpOpenViewButton
                  onClick={onLineUpRedirection}
                  sportId={sportId}
                  isNew={SportsWithLineUpNewTag.indexOf(sportId) !== -1}
                />
              ) : (
                <MatchCardMoreBetsButton moreBets={moreBets} />
              )}
            </AbsoluteRight>
          </WrapTitle>
          <BetGroupFactoryContainer
            betCategoryId={betCategoryId}
            expandId={expandId}
            betsData={betsData}
            matchId={matchId}
            showAlternativeTitle
          />
        </InnerWrap>
      </StyledLink>
    </Wrapper>
  );
};

export default React.memo<Props>(MatchCardOutright);
